import {annotationUuid} from "../data/model/index.js";

export const HIGHLIGHT_MARK_CLASS = "js-aa-highlight";
export const HIGHLIGHT_TOGGLE_CLASS = "aa-highlight-toggle";

const HIGH_CONTRAST_DARK_HIGHLIGHT_CLASS = "aa-high-contrast-dark-highlight";
const HIGH_CONTRAST_LIGHT_HIGHLIGHT_CLASS = "aa-high-contrast-light-highlight";

/**
 * For use only when the highlight color type is high contrast.
 * [See src/website-behaviors/index.js::renderHighlight()]
 *
 * If the marker (highlight element that shows the highlight color) doesn't have
 * a color class on it yet, this will try to choose the best color for it (dark
 * or light) and add that class to the marker.
 *
 * The dark highlight class gives a black highlight background with white text.
 * The light highlight class gives a white highlight background with dark gray text.
 *
 * If the text is transparent (such as on a PDF's text layer), assume that the
 * document background is light colored, and always use the dark highlight class.
 *
 * For normal visible text, this function looks at the text color, and assumes
 * that the background color of the text has enough contrast with the text color.
 * Then it will assign the highlight color that gives the most contrast with the
 * assumed background color.
 *
 * For example, if the text color is black, assume the page background is very
 * light. Therefore, we give the highlight the dark class, so that the black
 * highlight will be visible against the light page color.
 *
 * The function uses the RGB components of the text color to calculate how far
 * it is from the halfway point of black (0, 0, 0) and white (255, 255, 255). If
 * it's closer to black, make the highlight dark. If it's closer to white, make
 * the highlight white.
 *
 * @param {Node}        node   a text node
 * @param {HTMLElement} marker a highlight <mark> element
 */
// @ts-expect-error FIXME
export const addHighContrastHighlightType = (node, marker) => {
	// If the marker already has a color decided for it, keep it. Otherwise it
	// may end up overwriting a previously decided color, if the node is a text
	// node inside an already highlighted segment
	if (marker.classList.contains(HIGH_CONTRAST_DARK_HIGHLIGHT_CLASS)
		|| marker.classList.contains(HIGH_CONTRAST_LIGHT_HIGHLIGHT_CLASS))
		return;

	const parentStyles = window.getComputedStyle(node.parentNode);

	// Assume opacity of 1 if it doesn't exist for some reason, such as in tests
	const opacity = parentStyles.opacity ? parseFloat(parentStyles.opacity) : 1;

	const textColorRGB_str = parentStyles.color;
	const textColorRGB_arr = textColorRGB_str.replace(/[^\d,.]/g, "")
		.split(",")
		.map(numberString => parseInt(numberString, 10));

	// Alpha is 1 if undefined
	const alpha = textColorRGB_arr[3] ?? 1;

	// Distance from rgb(0, 0, 0)
	const euclidianDistance = Math.sqrt(Math.pow(textColorRGB_arr[0], 2) + Math.pow(textColorRGB_arr[1], 2) + Math.pow(textColorRGB_arr[2], 2));

	// Avoid dividing by 0. If alpha or opacity is 0, the text is transparent.
	// We'll just assume it's a PDF with a transparent text layer or something
	// similar, and use dark highlights
	const isTextTransparent = (alpha === 0 || opacity === 0);

	// To find the most "average" color between rgb(0, 0, 0) and rgb(255, 255, 255):
	// (255 - 0)/2 = 127.5
	// sqrt(127.5^2 + 127.5^2 + 127.5^2) = 220.836478
	if (isTextTransparent || (euclidianDistance / (alpha * opacity) < 220.84))
		marker.classList.add(HIGH_CONTRAST_DARK_HIGHLIGHT_CLASS);
	else
		marker.classList.add(HIGH_CONTRAST_LIGHT_HIGHLIGHT_CLASS);
};

/**
 * Get the keyboard accessible toggle element for a given annotation uuid.
 */
export const getHighlightToggle = (uuid: annotationUuid | string): HTMLElement | null => {
	return document.querySelector(`.${HIGHLIGHT_TOGGLE_CLASS}[data-uuid="${uuid}"]`);
};

/**
 * Get the rendered highlight mark element for a given annotation uuid.
 */
export const getHighlightMark = (uuid: annotationUuid | string): HTMLElement | null => {
	return document.querySelector(`.${HIGHLIGHT_MARK_CLASS}[data-uuid="${uuid}"]`);
};
